import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface PixelConfig {
  pixel_id: string;
  is_enabled: boolean;
}

export const useFacebookPixel = () => {
  const [pixelConfig, setPixelConfig] = useState<PixelConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pixelLoaded, setPixelLoaded] = useState(false);
  const pixelInitialized = useRef(false);
  const lastEventTime = useRef<Map<string, number>>(new Map());

  // 🆕 獲取或生成用戶唯一標識
  const getUserIdentifier = (): string => {
    let userId = localStorage.getItem('fb_pixel_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('fb_pixel_user_id', userId);
    }
    return userId;
  };

  // 🆕 獲取用戶 IP（從 Supabase 或第三方服務）
  const getUserIP = async (): Promise<string> => {
    try {
      // 嘗試從 ipify 獲取 IP
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unknown';
    } catch (error) {
      console.log('無法獲取 IP，使用預設值');
      return 'unknown';
    }
  };

  // 🆕 生成事件唯一鍵（基於 IP + Cookie + 事件名稱 + 內容）
  const generateEventKey = async (eventName: string, contentName?: string): Promise<string> => {
    const userId = getUserIdentifier();
    const ip = await getUserIP();
    const content = contentName || 'default';
    return `${ip}_${userId}_${eventName}_${content}`;
  };

  // 🆕 檢查事件是否已記錄（根據事件類型設定不同的冷卻時間）
  const isEventRecorded = async (eventName: string, contentName?: string): Promise<boolean> => {
    const eventKey = await generateEventKey(eventName, contentName);
    const recordedEvents = JSON.parse(localStorage.getItem('fb_pixel_recorded_events') || '{}');
    
    const now = Date.now();
    const recordTime = recordedEvents[eventKey];
    
    // 🔥 根據事件類型設定不同的冷卻時間
    let cooldownTime = 24 * 60 * 60 * 1000; // 預設 24 小時
    
    switch (eventName) {
      case 'PageView':
        cooldownTime = 30 * 60 * 1000; // PageView: 30 分鐘
        break;
      case 'ViewContent':
        cooldownTime = 60 * 60 * 1000; // ViewContent: 1 小時
        break;
      case 'InitiateCheckout':
        cooldownTime = 2 * 60 * 60 * 1000; // InitiateCheckout: 2 小時
        break;
      case 'Lead':
        cooldownTime = 24 * 60 * 60 * 1000; // Lead: 24 小時（保持嚴格）
        break;
    }
    
    // 如果在冷卻期內已記錄過，返回 true
    if (recordTime && (now - recordTime) < cooldownTime) {
      const remainingMinutes = Math.ceil((cooldownTime - (now - recordTime)) / (60 * 1000));
      console.log(`⏭️ 事件已記錄（${eventName}），跳過重複追蹤（還需等待 ${remainingMinutes} 分鐘）`);
      return true;
    }
    
    return false;
  };

  // 🆕 標記事件已記錄
  const markEventRecorded = async (eventName: string, contentName?: string) => {
    const eventKey = await generateEventKey(eventName, contentName);
    const recordedEvents = JSON.parse(localStorage.getItem('fb_pixel_recorded_events') || '{}');
    
    // 清理 7 天前的舊記錄
    const now = Date.now();
    Object.keys(recordedEvents).forEach(key => {
      if (now - recordedEvents[key] > 7 * 24 * 60 * 60 * 1000) {
        delete recordedEvents[key];
      }
    });
    
    // 記錄新事件
    recordedEvents[eventKey] = now;
    localStorage.setItem('fb_pixel_recorded_events', JSON.stringify(recordedEvents));
  };

  // 載入 Pixel 設定
  useEffect(() => {
    loadPixelConfig();
  }, []);

  const loadPixelConfig = async () => {
    try {
      console.log('🔄 開始載入 Facebook Pixel 設定...');
      
      const { data, error } = await supabase
        .from('brand_settings')
        .select('fb_pixel_id, fb_pixel_enabled')
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST301' || error.code === 'PGRST302' || error.code === 'PGRST303') {
          console.log('ℹ️ Facebook Pixel 設定載入失敗（認證問題），跳過追蹤');
        } else {
          console.log('ℹ️ Facebook Pixel 設定載入失敗，跳過追蹤');
        }
        setIsLoading(false);
        return;
      }

      if (data && data.fb_pixel_enabled && data.fb_pixel_id) {
        console.log('✅ Facebook Pixel 已啟用:', data.fb_pixel_id);
        setPixelConfig({
          pixel_id: data.fb_pixel_id,
          is_enabled: data.fb_pixel_enabled
        });
        initializeFacebookPixel(data.fb_pixel_id);
      } else {
        console.log('ℹ️ Facebook Pixel 未啟用或未設定');
      }
    } catch (error) {
      console.log('ℹ️ Facebook Pixel 設定載入失敗，跳過追蹤');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeFacebookPixel = (pixelId: string) => {
    if (pixelInitialized.current) {
      console.log('ℹ️ Facebook Pixel 已經初始化過了');
      setPixelLoaded(true);
      return;
    }

    try {
      if (window.fbq) {
        console.log('ℹ️ Facebook Pixel 腳本已存在');
        pixelInitialized.current = true;
        setPixelLoaded(true);
        return;
      }

      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq('init', pixelId);
      pixelInitialized.current = true;
      setPixelLoaded(true);
      console.log('✅ Facebook Pixel 初始化完成，Pixel ID:', pixelId);
    } catch (error) {
      console.error('❌ Facebook Pixel 初始化失敗:', error);
      setPixelLoaded(false);
    }
  };

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
    };
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  };

  const shouldTrackEvent = (eventKey: string, cooldownMs: number = 10000): boolean => {
    const now = Date.now();
    const lastTime = lastEventTime.current.get(eventKey);
    
    if (lastTime && (now - lastTime) < cooldownMs) {
      console.log(`⏭️ 事件 ${eventKey} 在冷卻期內，跳過（${Math.round((now - lastTime) / 1000)}秒前已觸發）`);
      return false;
    }
    
    lastEventTime.current.set(eventKey, now);
    return true;
  };

  const saveEventToDatabase = async (eventName: string, eventData?: any) => {
    try {
      // 🆕 生成事件唯一鍵
      const userId = eventData?.user_id || getUserIdentifier();
      const ip = eventData?.ip_address || 'unknown';
      const contentName = eventData?.content_name || eventData?.url || 'default';
      const eventKey = `${ip}_${userId}_${eventName}_${contentName}`;
      
      // 🆕 檢查資料庫中是否已存在相同事件（24小時內）
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      const { data: existingEvents, error: checkError } = await supabase
        .from('fb_pixel_events')
        .select('id, created_at')
        .eq('event_key', eventKey)
        .gte('created_at', twentyFourHoursAgo.toISOString())
        .limit(1);
      
      if (checkError) {
        console.log('ℹ️ 無法檢查重複事件，繼續儲存');
      } else if (existingEvents && existingEvents.length > 0) {
        console.log(`⏭️ 資料庫中已存在相同事件（${eventName}），跳過儲存`);
        return;
      }
      
      // 🆕 儲存事件（包含 event_key）
      const { error } = await supabase.from('fb_pixel_events').insert({
        event_name: eventName,
        event_data: eventData || {},
        event_key: eventKey,
        created_at: new Date().toISOString(),
      });

      if (error) {
        console.log('ℹ️ FB Pixel 事件儲存失敗，跳過');
      } else {
        console.log('✅ FB Pixel 事件已儲存到資料庫:', eventName);
      }
    } catch (error) {
      console.log('ℹ️ FB Pixel 事件儲存失敗，跳過');
    }
  };

  // 🆕 保存事件到 localStorage（用於調試面板）
  const saveEventToLocalStorage = (eventName: string, eventData?: any) => {
    try {
      const today = new Date().toLocaleDateString('zh-TW');
      const events = JSON.parse(localStorage.getItem('fb_pixel_events') || '{}');
      
      if (!events[today]) {
        events[today] = [];
      }
      
      events[today].push({
        name: eventName,
        data: eventData,
        time: new Date().toISOString(),
      });
      
      // 清理 7 天前的數據
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      Object.keys(events).forEach(date => {
        const eventDate = new Date(date.split('/').reverse().join('-'));
        if (eventDate < sevenDaysAgo) {
          delete events[date];
        }
      });
      
      localStorage.setItem('fb_pixel_events', JSON.stringify(events));
    } catch (error) {
      console.error('保存事件到 localStorage 失敗:', error);
    }
  };

  // 追蹤 PageView
  const trackPageView = useCallback(async () => {
    console.log('🎯 trackPageView 被調用');
    
    if (!pixelConfig?.is_enabled || !pixelLoaded) {
      console.log('⚠️ Pixel 未啟用或未載入，跳過 PageView 追蹤');
      return;
    }

    // 🆕 檢查是否已記錄
    if (await isEventRecorded('PageView', window.location.pathname)) {
      return;
    }

    const eventKey = `pageview_${window.location.pathname}`;
    if (!shouldTrackEvent(eventKey, 30000)) {
      return;
    }

    try {
      const utmParams = getUTMParams();
      const deviceType = getDeviceType();
      const userId = getUserIdentifier();
      const ip = await getUserIP();
      
      const eventData = {
        ...utmParams,
        device_type: deviceType,
        user_id: userId,
        ip_address: ip,
      };
      
      window.fbq('track', 'PageView', eventData);
      console.log('✅ Facebook Pixel PageView 事件已觸發', eventData);
      
      saveEventToDatabase('PageView', { 
        url: window.location.href,
        ...eventData,
      });
      
      saveEventToLocalStorage('PageView', eventData);
      
      // 🆕 標記事件已記錄
      await markEventRecorded('PageView', window.location.pathname);
    } catch (error) {
      console.error('❌ 追蹤 PageView 失敗:', error);
    }
  }, [pixelConfig, pixelLoaded]);

  // 追蹤 ViewContent
  const trackViewContent = useCallback(async (contentName: string, contentType: string) => {
    console.log('🎯 trackViewContent 被調用:', contentName, contentType);
    
    if (!pixelConfig?.is_enabled || !pixelLoaded) {
      console.log('⚠️ Pixel 未啟用或未載入，跳過 ViewContent 追蹤');
      return;
    }

    // 🆕 檢查是否已記錄
    if (await isEventRecorded('ViewContent', contentName)) {
      return;
    }

    const eventKey = `viewcontent_${contentName}`;
    if (!shouldTrackEvent(eventKey, 60000)) {
      return;
    }

    try {
      const utmParams = getUTMParams();
      const deviceType = getDeviceType();
      const userId = getUserIdentifier();
      const ip = await getUserIP();
      
      const eventData = {
        content_name: contentName,
        content_type: contentType,
        ...utmParams,
        device_type: deviceType,
        user_id: userId,
        ip_address: ip,
      };
      
      window.fbq('track', 'ViewContent', eventData);
      console.log('✅ Facebook Pixel ViewContent 事件已觸發');
      
      saveEventToDatabase('ViewContent', eventData);
      saveEventToLocalStorage('ViewContent', eventData);
      
      // 🆕 標記事件已記錄
      await markEventRecorded('ViewContent', contentName);
    } catch (error) {
      console.error('❌ 追蹤 ViewContent 失敗:', error);
    }
  }, [pixelConfig, pixelLoaded]);

  // 追蹤 InitiateCheckout
  const trackInitiateCheckout = useCallback(async (contentName: string, contentType: string) => {
    console.log('🎯 trackInitiateCheckout 被調用:', contentName, contentType);
    
    if (!pixelConfig?.is_enabled || !pixelLoaded) {
      console.log('⚠️ Pixel 未啟用或未載入，跳過 InitiateCheckout 追蹤');
      return;
    }

    // 🆕 檢查是否已記錄
    if (await isEventRecorded('InitiateCheckout', contentName)) {
      return;
    }

    const eventKey = `initiatecheckout_${contentName}`;
    if (!shouldTrackEvent(eventKey, 60000)) {
      return;
    }

    try {
      const utmParams = getUTMParams();
      const deviceType = getDeviceType();
      const userId = getUserIdentifier();
      const ip = await getUserIP();
      
      const eventData = {
        content_name: contentName,
        content_type: contentType,
        ...utmParams,
        device_type: deviceType,
        user_id: userId,
        ip_address: ip,
      };
      
      window.fbq('track', 'InitiateCheckout', eventData);
      console.log('✅ Facebook Pixel InitiateCheckout 事件已觸發');
      
      saveEventToDatabase('InitiateCheckout', eventData);
      saveEventToLocalStorage('InitiateCheckout', eventData);
      
      // 🆕 標記事件已記錄
      await markEventRecorded('InitiateCheckout', contentName);
    } catch (error) {
      console.error('❌ 追蹤 InitiateCheckout 失敗:', error);
    }
  }, [pixelConfig, pixelLoaded]);

  // 追蹤 Lead
  const trackLead = useCallback(async (contentName: string, value?: number, customData?: any) => {
    console.log('🎯 trackLead 被調用:', contentName, value, customData);
    
    if (!pixelConfig?.is_enabled || !pixelLoaded) {
      console.log('⚠️ Pixel 未啟用或未載入，跳過 Lead 追蹤');
      return;
    }

    // 🆕 檢查是否已記錄（Lead 事件允許重複，但 24 小時內同一內容只記錄一次）
    if (await isEventRecorded('Lead', contentName)) {
      return;
    }

    try {
      const utmParams = getUTMParams();
      const deviceType = getDeviceType();
      const userId = getUserIdentifier();
      const ip = await getUserIP();
      
      const timeOnPage = Math.round((Date.now() - (window as any).pageLoadTime) / 1000);
      
      const eventData = {
        content_name: contentName,
        value: value || 0,
        currency: 'TWD',
        ...utmParams,
        device_type: deviceType,
        time_on_page: timeOnPage,
        user_id: userId,
        ip_address: ip,
        ...customData,
      };
      
      window.fbq('track', 'Lead', eventData);
      console.log('✅ Facebook Pixel Lead 事件已觸發', eventData);
      
      saveEventToDatabase('Lead', eventData);
      saveEventToLocalStorage('Lead', eventData);
      
      // 🆕 標記事件已記錄
      await markEventRecorded('Lead', contentName);
    } catch (error) {
      console.error('❌ 追蹤 Lead 失敗:', error);
    }
  }, [pixelConfig, pixelLoaded]);

  return {
    isLoading,
    pixelConfig,
    pixelLoaded,
    trackPageView,
    trackViewContent,
    trackInitiateCheckout,
    trackLead,
  };
};

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    pageLoadTime: number;
  }
}
